/**
 * TaskChangeCard - Rich card display for a single task's changes.
 *
 * Task-specific layout using shared change utilities:
 * - Header with task name, group badge, type badge
 * - Key fields grid (due date, CMS status, CMS reason, template)
 * - Collapsible reference IDs section
 * - Amber change indicators with old-value tooltips
 * - Transfer checkboxes + "Mark as Transferred" button
 */

import { ClipboardList } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type {
  ChangedTaskEntity,
  ChangedTaskUpdateEntity,
  TaskGroups,
  TaskType,
} from '@/services/api/generated/crossMatterChangesService';

import {
  buildCheckableItems,
  type CheckableFieldConfig,
} from '../utils/buildCheckableItems';
import { formatEnumValue } from '../utils/formatting';
import { useEntityChanges } from '../utils/useEntityChanges';
import { useTransferAction } from '../utils/useTransferAction';
import { useTransferChecklist } from '../utils/useTransferChecklist';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { CollapsibleSection } from './CollapsibleSection';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { EntityField } from './EntityField';
import { EventsFallbackWithChecklist } from './EventsFallbackWithChecklist';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';
import { TaskUpdateChangeCard } from './TaskUpdateChangeCard';
import { TransferButton } from './TransferButton';
import { TransferCheckbox } from './TransferCheckbox';

// ============================================================================
// Task-specific config
// ============================================================================

const KEY_FIELDS: FieldConfig[] = [
  { key: 'due_date', label: 'Due Date', isDate: true },
];

const REFERENCE_FIELDS: { key: string; label: string }[] = [
  { key: 'assignee_id', label: 'Assignee ID' },
  { key: 'created_by_id', label: 'Created By ID' },
  { key: 'medical_note_id', label: 'Medical Note ID' },
  { key: 'coverage_id', label: 'Coverage ID' },
  { key: 'police_report_id', label: 'Police Report ID' },
  { key: 'treatment_record_request_id', label: 'Treatment Request ID' },
];

const TASK_CHECKABLE_FIELDS: CheckableFieldConfig[] = [
  { key: 'group', type: 'field' },
  { key: 'type', type: 'field' },
  { key: 'due_date', type: 'field' },
  { key: 'assignee_id', type: 'field' },
  { key: 'created_by_id', type: 'field' },
  { key: 'medical_note_id', type: 'field' },
  { key: 'coverage_id', type: 'field' },
  { key: 'police_report_id', type: 'field' },
  { key: 'treatment_record_request_id', type: 'field' },
];

// ============================================================================
// Main Component
// ============================================================================

export const TaskChangeCard: React.FC<{
  entity: ChangedTaskEntity;
  taskUpdates?: ChangedTaskUpdateEntity[];
}> = ({ entity, taskUpdates = [] }) => {
  const displayData =
    entity.change_type === 'DELETED'
      ? (entity.old ?? entity.current)
      : (entity.current ?? entity.old);

  const changes = useEntityChanges(
    entity.change_type,
    entity.old,
    entity.current,
  );

  const checkableItems = React.useMemo<string[]>(() => {
    const items = buildCheckableItems(
      entity.change_type,
      displayData as Record<string, unknown> | undefined,
      entity.events,
      changes,
      TASK_CHECKABLE_FIELDS,
    );
    // Always require status change acknowledgment when present
    if (entity.events.some((e) => e.event_code === 'task_status_changed')) {
      items.push('event.task_status_changed');
    }
    return items;
  }, [entity.change_type, entity.events, changes, displayData]);

  const checklist = useTransferChecklist(entity.change_type, checkableItems);

  // Track nested task update readiness
  const [childReady, setChildReady] = React.useState<Record<string, boolean>>(
    {},
  );
  const handleChildReady = React.useCallback(
    (entityId: string, ready: boolean) => {
      setChildReady((prev) => ({ ...prev, [entityId]: ready }));
    },
    [],
  );

  const hasTaskUpdates = taskUpdates.length > 0;
  const hasTaskUpdateChanges = taskUpdates.some(
    (tu) => tu.change_type !== 'SAME',
  );
  const changedTaskUpdates = taskUpdates.filter(
    (tu) => tu.change_type !== 'SAME',
  );
  const sameTaskUpdates = taskUpdates.filter(
    (tu) => tu.change_type === 'SAME',
  );
  const [showUnchangedUpdates, setShowUnchangedUpdates] = React.useState(false);

  // Transfer button: own items + all nested task update items must be ready
  const allChildrenReady = taskUpdates.every(
    (tu) => childReady[tu.entity_id] !== false,
  );

  // For SAME tasks with changed children: show acknowledge checkbox so the
  // user can mark the task for transfer even though the task itself is unchanged.
  const needsChildTransfer =
    entity.change_type === 'SAME' && hasTaskUpdateChanges;

  const effectiveChecklist = React.useMemo(
    () =>
      needsChildTransfer
        ? {
            ...checklist,
            showAcknowledge: true,
            buttonEnabled: checklist.acknowledgeChecked,
          }
        : checklist,
    [checklist, needsChildTransfer],
  );

  const transferButtonEnabled =
    effectiveChecklist.buttonEnabled && allChildrenReady;
  // Show button if task needs it OR if there are task updates with changes
  const showTransferButton =
    checklist.showButton || hasTaskUpdateChanges;

  // Collect event IDs for transfer: own events + all nested task update events
  const transferEventIds = React.useMemo(
    () => [
      ...entity.events.map((e) => e.id),
      ...taskUpdates.flatMap((tu) => tu.events.map((e) => e.id)),
    ],
    [entity.events, taskUpdates],
  );
  const transfer = useTransferAction(transferEventIds);

  if (!displayData) return null;

  const data = displayData as unknown as Record<string, unknown>;

  // Header fields
  const group = displayData.group as TaskGroups | undefined;
  const type = displayData.type as TaskType | undefined;
  const groupChanged = changes.isFieldChanged('group');
  const typeChanged = changes.isFieldChanged('type');

  // Status changed detection from events
  const hasStatusChanged = entity.events.some(
    (e) => e.event_code === 'task_status_changed',
  );

  // Reference fields visibility
  const visibleReferenceFields = REFERENCE_FIELDS.filter(
    (f) => data[f.key] || changes.isFieldChanged(f.key),
  );
  const changedReferenceFields = visibleReferenceFields.filter((f) =>
    changes.isFieldChanged(f.key),
  );
  const unchangedReferenceFields = visibleReferenceFields.filter(
    (f) => !changes.isFieldChanged(f.key),
  );

  // Events fallback
  const isUpdatedWithoutDiffs =
    entity.change_type === 'UPDATED' && changes.totalChanges === 0;

  const hasExpandedContent =
    KEY_FIELDS.some((f) => {
      const val = data[f.key];
      const hasValue = val !== null && val !== undefined && val !== '';
      return hasValue || changes.isFieldChanged(f.key);
    }) ||
    groupChanged ||
    typeChanged ||
    hasStatusChanged ||
    visibleReferenceFields.length > 0 ||
    isUpdatedWithoutDiffs;

  // Compute unchanged content for ShowMoreSection
  const hasUnchangedContent =
    (entity.change_type === 'UPDATED' || entity.change_type === 'SAME') &&
    (KEY_FIELDS.some((f) => {
      const val = data[f.key];
      const hasValue = val !== null && val !== undefined && val !== '';
      return hasValue && !changes.isFieldChanged(f.key);
    }) ||
      unchangedReferenceFields.length > 0);

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={entity.name}
      description={entity.description}
      icon={ClipboardList}
      iconBgClassName="bg-violet-50"
      iconTextClassName="text-violet-600"
      badges={
        <>
          {group && (
            <Badge color="zinc" className="text-[10px] shrink-0">
              {group}
            </Badge>
          )}
          {type && formatEnumValue(type) !== group && (
            <Badge color="zinc" className="text-[10px] shrink-0">
              {formatEnumValue(type)}
            </Badge>
          )}
        </>
      }
      statusBadge={
        hasStatusChanged ? (
          <Badge color="amber" className="text-[10px] shrink-0">
            Status Changed
          </Badge>
        ) : undefined
      }
      changeCount={changes.totalChanges}
      eventCount={entity.events.length}
      checklist={effectiveChecklist}
      hasExpandedContent={hasExpandedContent || hasTaskUpdates}
    >
      {/* Status changed indicator — always shown */}
      {hasStatusChanged && (
        <div className="flex items-center gap-2">
          <p className="flex items-center gap-1.5 text-xs text-gray-900 px-1 flex-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
            Task Status Changed
          </p>
          {checklist.showCheckboxes && (
            <TransferCheckbox
              checked={checklist.isChecked('event.task_status_changed')}
              onToggle={() => checklist.toggle('event.task_status_changed')}
            />
          )}
        </div>
      )}

      {/* Events fallback (excluding status change which is shown above) */}
      {isUpdatedWithoutDiffs && (
        <EventsFallbackWithChecklist
          events={entity.events.filter(
            (e) => e.event_code !== 'task_status_changed',
          )}
          checklist={checklist}
        />
      )}

      {/* Changed header fields shown prominently */}
      {groupChanged && group && (
        <ChangedFieldHighlight
          label="Group"
          fieldKey="group"
          changes={changes}
          checklist={checklist}
        >
          <span className="text-sm text-gray-900">{group}</span>
        </ChangedFieldHighlight>
      )}

      {typeChanged && type && (
        <ChangedFieldHighlight
          label="Type"
          fieldKey="type"
          changes={changes}
          checklist={checklist}
        >
          <span className="text-sm text-gray-900">
            {formatEnumValue(type)}
          </span>
        </ChangedFieldHighlight>
      )}

      {/* Key fields grid */}
      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        checklist={checklist}
        changeType={entity.change_type}
      />

      {/* Reference IDs (changed only) */}
      {changedReferenceFields.length > 0 && (
        <CollapsibleSection
          title="References"
          count={changedReferenceFields.length}
          changeType={entity.change_type}
          hasChanges
        >
          {changedReferenceFields.map((f) => (
            <div key={f.key} className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <EntityField
                  label={f.label}
                  value={data[f.key]}
                  fieldKey={f.key}
                  changes={changes}
                />
              </div>
              {checklist.showCheckboxes && changes.isFieldChanged(f.key) && (
                <TransferCheckbox
                  checked={checklist.isChecked(f.key)}
                  onToggle={() => checklist.toggle(f.key)}
                  className="mt-2.5"
                />
              )}
            </div>
          ))}
        </CollapsibleSection>
      )}

      {/* Show More Section for unchanged content */}
      {hasUnchangedContent && (
        <ShowMoreSection changeType={entity.change_type}>
          {/* Unchanged key fields */}
          <KeyFieldsGridUnchanged
            fields={KEY_FIELDS}
            data={data}
            changes={changes}
            checklist={checklist}
          />

          {/* Unchanged reference IDs */}
          {unchangedReferenceFields.length > 0 && (
            <CollapsibleSection
              title="References"
              count={unchangedReferenceFields.length}
              changeType={entity.change_type}
              hasChanges={false}
            >
              {unchangedReferenceFields.map((f) => (
                <div key={f.key} className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <EntityField
                      label={f.label}
                      value={data[f.key]}
                      fieldKey={f.key}
                      changes={changes}
                    />
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          )}
        </ShowMoreSection>
      )}

      {/* Nested task updates — condensed rows */}
      {hasTaskUpdates && (
        <CollapsibleSection
          title="Task Updates"
          count={taskUpdates.length}
          hasChanges={hasTaskUpdateChanges}
          changeType={entity.change_type}
        >
          {changedTaskUpdates.map((tu) => (
            <TaskUpdateChangeCard
              key={tu.entity_id}
              entity={tu}
              nested
              condensed
              onTransferReady={handleChildReady}
            />
          ))}
          {sameTaskUpdates.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowUnchangedUpdates((v) => !v)}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors px-2 py-1"
              >
                {showUnchangedUpdates
                  ? 'Hide unchanged'
                  : `Show ${sameTaskUpdates.length} unchanged`}
              </button>
              {showUnchangedUpdates &&
                sameTaskUpdates.map((tu) => (
                  <TaskUpdateChangeCard
                    key={tu.entity_id}
                    entity={tu}
                    nested
                    condensed
                    onTransferReady={handleChildReady}
                  />
                ))}
            </>
          )}
        </CollapsibleSection>
      )}

      {/* Transfer button */}
      {showTransferButton && (
        <TransferButton
          enabled={transferButtonEnabled}
          onClick={transfer.handleTransfer}
          onSkip={transfer.handleSkip}
          isLoading={transfer.isLoading}
          status={transfer.status}
        />
      )}
    </EntityChangeCardShell>
  );
};
